import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useContext,
  useEffect,
} from "react";

import {
  Box,
  TextField,
  InputLabel,
  Grid,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material/";
import SearchIcon from "@mui/icons-material/Search";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CachedIcon from "@mui/icons-material/Cached";
import { Edit } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import FileSaver from "file-saver";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../util/state/globalAction";
import { snackbarTypes } from "../../../util/state/globalVariables";
import "./genericModels.css";
import "./carSegmentSupplyDetermination.css";
import { ContextOperation } from "../context/OperationContext";
import { useHasScope } from "../../../config/globalConfig";
import { carSegmentSupplyActions } from "../service/carSegmentSupply.actions";
import {
  CellValueChangedEvent,
  ColDef,
  ICellRendererParams,
} from "ag-grid-community";
import {
  GetAllSupplyPayload,
  UpdatePercentagePayload,
} from "../service/carSegmentSupplyInterfaces";

import FleetPlanningToolbar from "./planning/FleetPlanningToolbar";
import { useTranslation } from "react-i18next";
import CustomLoader from "../../../util/component/CustomLoader";
import * as Mui from "@mui/material";
import Replay from "@mui/icons-material/Replay";

export const CarSegmentSupplyDetermination = (props: any) => {
  const hasScope = useHasScope;
  const carSegmentSupply = carSegmentSupplyActions();
  const { dispatchGlobalData } = useContext(GlobalContext);
  const { t } = useTranslation(["operation"]);
  const gridRef = useRef<AgGridReact>(null);

  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  // const [props?.dataSearchFormThree?.startDate, setStartDate] = useState<Dayjs | null>(null);
  // const [props?.dataSearchFormThree?.endDate, setEndDate] = useState<Dayjs | null>(null);
  const [defaultDate] = useState<Dayjs>(dayjs().add(1, "months"));
  const [rowData, setRowData] = useState<any>([]);
  const [columnDefs, setColumnDefs] = useState<any>([]);
  const [tableHeader, setTableHeader] = useState<string>("");
  const [tableSubHeader, setTableSubHeader] = useState<string>("");
  const [editedPercentages, setEditedPercentages] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { tenant } = useContext(ContextOperation);

  const rowHeight = (params: { data: any }) =>
    params.data.sapCarSegment === "TOTAL" ? 52 : 42;

  const defaultColDef: ColDef = {
    suppressMovable: true,
  };

  const cellClassRules = {
    rfColumn: (params: { colDef: any }) =>
      params.colDef.headerName ===
      t("fleetPlanning.carSegmentSupplyDetermination.rf"),
    fixedColumns: (params: { colDef: any }) =>
      params.colDef.headerName ===
        t("fleetPlanning.carSegmentSupplyDetermination.carSegment") ||
      params.colDef.headerName ===
        t("fleetPlanning.carSegmentSupplyDetermination.startFleet"),
    fixedColumnBorder: (params: { colDef: any }) =>
      params.colDef.headerName ===
      t("fleetPlanning.carSegmentSupplyDetermination.startFleet"),
    fixedColumnsTotal: (params: { data: any }) =>
      params.data.sapCarSegment === "TOTAL",
    greenBackground: (params: { colDef: any; data: any }) =>
      params.colDef.headerName ===
        t("fleetPlanning.carSegmentSupplyDetermination.buying") &&
      params.data.sapCarSegment === "TOTAL",
    bordersRight: (params: { colDef: any }) =>
      params.colDef.headerName ===
        t("fleetPlanning.carSegmentSupplyDetermination.rf") ||
      params.colDef.headerName ===
        t("fleetPlanning.carSegmentSupplyDetermination.startFleet"),
  };

  const rowClassRules = useMemo(() => {
    return {
      totalsRow: (params: any) => {
        return params.data.sapCarSegment === "TOTAL";
      },
    };
  }, []);

  const basicColumnDefsChildren: any = [
    {
      field: "sapCarSegmentParent",
      headerName: " ",
      children: [
        {
          field: "sapCarSegment",
          headerName: t(
            "fleetPlanning.carSegmentSupplyDetermination.carSegment"
          ),
          width: 150,
          pinned: "left",
          cellClassRules: cellClassRules,
        },
      ],
    },
    {
      field: "startFleetParent",
      headerName: " ",
      children: [
        {
          field: "startFleet",
          headerName: t(
            "fleetPlanning.carSegmentSupplyDetermination.startFleet"
          ),
          width: 120,
          pinned: "left",
          cellClassRules: cellClassRules,
          headerClass: ["fixedColumnBorder", "bordersRight"],
        },
      ],
    },
  ];

  const handleColumnDefs = (data: any) => {
    return data.map((el: any) => {
      return {
        field: el.month,
        headerName: dayjs()
          .year(el.year)
          .month(el.month - 1)
          .format("MMMM"),
        headerClass: "bordersRight",
        children: [
          {
            headerName: t("fleetPlanning.carSegmentSupplyDetermination.buying"),
            field: `${el.year}-${el.month}-ppq`,
            width: 100,
            cellClassRules: cellClassRules,
            cellRenderer: (params: ICellRendererParams) => {
              return (
                <>
                  {params.data.sapCarSegment !== "TOTAL" && (
                    <span>{params.value}</span>
                  )}
                  {params.data.sapCarSegment === "TOTAL" && (
                    <span className="coloredGreenTotal">{params.value}</span>
                  )}
                </>
              );
            },
          },
          {
            headerName: t(
              "fleetPlanning.carSegmentSupplyDetermination.selling"
            ),
            field: `${el.year}-${el.month}-sold`,
            width: 100,
            cellRenderer: (params: ICellRendererParams) => {
              return (
                <>
                  {params.data.sapCarSegment !== "TOTAL" && (
                    <span>{params.value}</span>
                  )}
                  {params.data.sapCarSegment === "TOTAL" && (
                    <span className="coloredRedTotal">{params.value}</span>
                  )}
                </>
              );
            },
          },
          {
            headerName: t("fleetPlanning.carSegmentSupplyDetermination.fleet"),
            field: `${el.year}-${el.month}-finalFleet`,
            width: 100,
          },
          {
            headerName: t("fleetPlanning.carSegmentSupplyDetermination.usage"),
            field: `${el.year}-${el.month}-usagePercentage`,
            width: 120,
            valueSetter: myValueSetter,
            editable: (params: ICellRendererParams) =>
              params.data.sapCarSegment === "TOTAL",
            cellRenderer: (params: ICellRendererParams) => {
              return (
                <span>
                  {params.value}%
                  {params.data.sapCarSegment === "TOTAL" && (
                    <IconButton
                      onClick={() =>
                        onClickEdit(`${el.year}-${el.month}-usagePercentage`)
                      }
                      sx={{
                        marginLeft: "10px",
                      }}
                    >
                      <Edit
                        sx={{
                          color: "#C8017D",
                        }}
                      />
                    </IconButton>
                  )}
                </span>
              );
            },
          },
          {
            headerName: t("fleetPlanning.carSegmentSupplyDetermination.rf"),
            field: `${el.year}-${el.month}-splittedRf`,
            width: 100,
            cellClassRules: cellClassRules,
            headerClass: ["rfColumn", "bordersRight"],
          },
        ],
      };
    });
  };

  const handleRowData = (data: any) => {
    return data.map((el: any) => {
      const data = el.carSegmentSupplyDeterminations.map((data: any) => {
        let allData: any = {};
        allData[`${data.year}-${data.month}-ppq`] = data.ppq ? data.ppq : "-";
        allData[`${data.year}-${data.month}-sold`] = data.sold
          ? data.sold
          : "-";
        allData[`${data.year}-${data.month}-finalFleet`] = data.finalFleet
          ? data.finalFleet
          : "-";
        allData[`${data.year}-${data.month}-usagePercentage`] =
          data.usagePercentage ? data.usagePercentage : "-";
        allData[`${data.year}-${data.month}-splittedRf`] = data.splittedRf
          ? data.splittedRf
          : "-";
        return allData;
      });
      return {
        sapCarSegment: el.carSegmentSupplyDeterminationKey.sapCarSegment,
        startFleet: el.carSegmentSupplyDeterminationKey.startFleet,
        ...Object.assign(
          {},
          ...(function _flatten(o): any {
            return [].concat(
              ...Object.keys(o).map((k) =>
                typeof o[k] === "object" ? _flatten(o[k]) : { [k]: o[k] }
              )
            );
          })(data)
        ),
      };
    });
  };

  const handleTotalData = (data: any, totalMonthFleet: any) => {
    let allData = data.map((el: any) => {
      let formattedData: any = {};
      formattedData[`${el.year}-${el.month}-ppq`] = el.ppq ? el.ppq : "-";
      formattedData[`${el.year}-${el.month}-sold`] = el.sold ? el.sold : "-";
      formattedData[`${el.year}-${el.month}-finalFleet`] = el.finalFleet
        ? el.finalFleet
        : "-";
      formattedData[`${el.year}-${el.month}-usagePercentage`] =
        el.usagePercentage ? `${el.usagePercentage}` : "-";
      formattedData[`${el.year}-${el.month}-splittedRf`] = el.splittedRf
        ? el.splittedRf
        : "-";
      return formattedData;
    });
    let spreadedData = {
      sapCarSegment: totalMonthFleet.sapCarSegment,
      startFleet: totalMonthFleet.startFleet,
      ...Object.assign(
        {},
        ...(function _flatten(o): any {
          return [].concat(
            ...Object.keys(o).map((k) =>
              typeof o[k] === "object" ? _flatten(o[k]) : { [k]: o[k] }
            )
          );
        })(allData)
      ),
    };
    return spreadedData;
  };

  const getAllData = () => {
    setLoading(true);

    let payload: GetAllSupplyPayload = {
      yearFrom: props?.dataSearchFormThree?.startDate
        ? props?.dataSearchFormThree?.startDate.year()
        : defaultDate.year(),
      yearTo: props?.dataSearchFormThree?.endDate
        ? props?.dataSearchFormThree?.endDate.year()
        : defaultDate.add(2, "months").year(),
      monthFrom: props?.dataSearchFormThree?.startDate
        ? +props?.dataSearchFormThree?.startDate.format("M")
        : +defaultDate?.format("M"),
      monthTo: props?.dataSearchFormThree?.endDate
        ? +props?.dataSearchFormThree?.endDate?.format("M")
        : +defaultDate?.add(2, "months").format("M"),
    };

    let diff = dayjs()
      .year(payload.yearTo)
      .month(payload.monthTo - 1)
      .diff(
        dayjs()
          .year(payload.yearFrom)
          .month(payload.monthFrom - 1),
        "months"
      );

    let diff12Months = dayjs()
      .year(payload.yearTo)
      .month(payload.monthTo - 1)
      .diff(dayjs(), "years");

    if (diff > 11) {
      setLoading(false);
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t("fleetPlanning.messages.majorPeriod"),
            type: snackbarTypes.info,
            show: true,
            autoClose: true,
          },
        },
      });
    } else if (diff < 0) {
      setLoading(false);
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t("fleetPlanning.messages.minorPeriod"),
            type: snackbarTypes.info,
            show: true,
            autoClose: true,
          },
        },
      });
    } else if (diff12Months > 1) {
      setLoading(false);
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t("fleetPlanning.messages.beyondPeriod"),
            type: snackbarTypes.info,
            show: true,
            autoClose: true,
          },
        },
      });
    } else {

      setColumnDefs([]);
      setRowData([]);
      
      carSegmentSupply
        .getAll(
          tenant,
          payload.yearFrom,
          payload.yearTo,
          payload.monthFrom,
          payload.monthTo
        )
        .then((response: any) => {
          let tableHeaderText = "";
          let tableSubHeaderText = "";
          if (
            props?.dataSearchFormThree?.startDate &&
            props?.dataSearchFormThree?.endDate
          ) {
            tableHeaderText = `${props?.dataSearchFormThree?.startDate.year()}`;
            tableSubHeaderText = `01/${props?.dataSearchFormThree?.startDate.format(
              "MM"
            )} - ${props?.dataSearchFormThree?.endDate
              .endOf("month")
              .format("DD")}/${props?.dataSearchFormThree?.endDate.format(
              "MM"
            )}`;
          } else {
            tableHeaderText = `${defaultDate.year()} `;
            tableSubHeaderText = `01/${defaultDate.format("MM")} - ${defaultDate
              .add(2, "months")
              .endOf("month")
              .format("DD")}/${defaultDate.add(2, "months").format("MM")}`;
          }
          setTableHeader(tableHeaderText);
          setTableSubHeader(tableSubHeaderText);

          const allColumns = [
            ...basicColumnDefsChildren,
            ...handleColumnDefs(response.totalCarSegmentSupplyDeterminations),
          ];
          setColumnDefs(allColumns);
          setRowData([
            ...handleRowData(response.carSegmentSupplyDeterminations),
            handleTotalData(
              response.totalCarSegmentSupplyDeterminations,
              response.totalMonthFleet
            ),
          ]);
          setLoading(false);
        })
        .catch((e: any) => {
          setLoading(false);
        });
    }
  };

  const handleDownloadExcel = async () => {
    setLoading(true);

    let payload: GetAllSupplyPayload = {
      yearFrom: props?.dataSearchFormThree?.startDate
        ? props?.dataSearchFormThree?.startDate.year()
        : defaultDate.year(),
      yearTo: props?.dataSearchFormThree?.endDate
        ? props?.dataSearchFormThree?.endDate.year()
        : defaultDate.add(2, "months").year(),
      monthFrom: props?.dataSearchFormThree?.startDate
        ? +props?.dataSearchFormThree?.startDate.format("M")
        : +defaultDate?.format("M"),
      monthTo: props?.dataSearchFormThree?.endDate
        ? +props?.dataSearchFormThree?.endDate?.format("M")
        : +defaultDate?.add(2, "months").format("M"),
    };

    carSegmentSupply
      .downloadExcel(
        tenant,
        payload.yearFrom,
        payload.yearTo,
        payload.monthFrom,
        payload.monthTo
      )
      .then((response: any) => {
        const filename =
          response.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
        setLoading(false);
      })
      .catch((e: any) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAllData();
  }, [props.setRefreshButton, props.dataSearchFormThree]);

  // const handleClearFilters = () => {
  //   //setStartDate(null);
  //   //setEndDate(null);
  //   getAllData();
  // };

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent) => {
      if (Number(params.newValue)) {
        const colId = params.column.getColId();
        const year = Number(colId.substr(0, 4));
        const month = Number(
          colId.slice(colId.indexOf("-") + 1, colId.lastIndexOf("-"))
        );

        var oldValues = [...editedPercentages];
        let foundPercentage = oldValues.find(
          (item: any) =>
            item.yearToUpdate === year && item.monthToUpdate === month
        );
        if (foundPercentage) {
          foundPercentage.newPercentage = Number(params.newValue);
        } else {
          oldValues.push({
            yearToUpdate: year,
            monthToUpdate: month,
            newPercentage: Number(params.newValue),
          });
        }
        setEditedPercentages(oldValues);
      }
    },
    [editedPercentages]
  );

  const myValueSetter = (params: any) => {
    if (!Number(params.newValue)) {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t("fleetPlanning.messages.validNumber"),
            type: snackbarTypes.error,
            show: true,
            autoClose: true,
          },
        },
      });
      return false;
    } else if (params.newValue > 100) {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t("fleetPlanning.messages.lessNumber"),
            type: snackbarTypes.error,
            show: true,
            autoClose: true,
          },
        },
      });
      return false;
    }

    params.data[params.colDef.field] = params.newValue;
    return true;
  };

  const onClickEdit = (columnName: string) => {
    if (gridRef.current) {
      gridRef.current.api.setFocusedCell(10, columnName);
      gridRef.current.api.startEditingCell({
        rowIndex: 10,
        colKey: columnName,
      });
    }
  };

  const handleEditData = () => {
    setLoading(true);
    let payload: UpdatePercentagePayload = {
      //tenant: "IT",
      yearFrom: props?.dataSearchFormThree?.startDate
        ? props?.dataSearchFormThree?.startDate.year()
        : defaultDate.year(),
      yearTo: props?.dataSearchFormThree?.endDate
        ? props?.dataSearchFormThree?.endDate.year()
        : defaultDate.add(2, "months").year(),
      monthFrom: props?.dataSearchFormThree?.startDate
        ? +props?.dataSearchFormThree?.startDate.format("M")
        : +defaultDate?.format("M"),
      monthTo: props?.dataSearchFormThree?.endDate
        ? +props?.dataSearchFormThree?.endDate?.format("M")
        : +defaultDate?.add(2, "months").format("M"),
      requests: [...editedPercentages],
    };
    carSegmentSupply
      .updatePercentage(tenant, payload)
      .then((response: any) => {
        if (response.totalMonthFleet) {
          setEditedPercentages([]);
          setRowData([
            ...handleRowData(response.carSegmentSupplyDeterminations),
            handleTotalData(
              response.totalCarSegmentSupplyDeterminations,
              response.totalMonthFleet
            ),
          ]);
        }
        setLoading(false);
      })
      .catch((error) => {
        dispatchGlobalData({
          type: setSnackbar,
          payload: {
            snackbar: {
              message: error.response.data.error,
              type: snackbarTypes.error,
              show: true,
              autoClose: true,
            },
          },
        });
        setLoading(false);
      });
  };

  const handleResetData = () => {
    setLoading(true);
    let payload: UpdatePercentagePayload = {
      yearFrom: props?.dataSearchFormThree?.startDate
        ? props?.dataSearchFormThree?.startDate.year()
        : defaultDate.year(),
      yearTo: props?.dataSearchFormThree?.endDate
        ? props?.dataSearchFormThree?.endDate.year()
        : defaultDate.add(2, "months").year(),
      monthFrom: props?.dataSearchFormThree?.startDate
        ? +props?.dataSearchFormThree?.startDate.format("M")
        : +defaultDate?.format("M"),
      monthTo: props?.dataSearchFormThree?.endDate
        ? +props?.dataSearchFormThree?.endDate?.format("M")
        : +defaultDate?.add(2, "months").format("M"),
    };

    carSegmentSupply.resetPercentage(tenant, payload).then((response: any) => {
      if (response.totalMonthFleet) {
        setRowData([
          ...handleRowData(response.carSegmentSupplyDeterminations),
          handleTotalData(
            response.totalCarSegmentSupplyDeterminations,
            response.totalMonthFleet
          ),
        ]);
      }
      setLoading(false);
    });
  };

  return (
    <>
      <Box className="fpOuterFilterBox">
        <CustomLoader loading={loading} />
        {/* <form>
          <Grid
            container
            spacing={2.5}
            alignItems="center"
            alignContent="center"
            direction="row"
          >
            <Grid xs item>
              <Box>
                <InputLabel className="carGenericFilterLabelClass"></InputLabel>
                <DatePicker
                  views={["year", "month"]}
                  label={t(
                    "fleetPlanning.carSegmentSupplyDetermination.props?.dataSearchFormThree?.startDate"
                  )}
                  value={props?.dataSearchFormThree?.startDate}
                  onChange={setStartDate}
                  renderInput={(params) => (
                    <TextField {...params} helperText={null} />
                  )}
                />
              </Box>
            </Grid>

            <Grid xs item>
              <Box>
                <InputLabel className="carGenericFilterLabelClass"></InputLabel>
                <DatePicker
                  views={["year", "month"]}
                  label={t(
                    "fleetPlanning.carSegmentSupplyDetermination.props?.dataSearchFormThree?.endDate"
                  )}
                  value={props?.dataSearchFormThree?.endDate}
                  InputProps={{
                    endAdornment: (
                      <DateRangeIcon sx={{ width: "18px", height: "18px" }} />
                    ),
                  }}
                  onChange={setEndDate}
                  renderInput={(params) => (
                    <TextField variant="standard" fullWidth {...params} />
                  )}
                />
              </Box>
            </Grid>

            <Grid xs item>
              <Box>&nbsp;</Box>
            </Grid>

            <Grid xs item>
              <Box>&nbsp;</Box>
            </Grid>

            <Grid xs item>
              <Box sx={{ justifyContent: "flex-end", display: "flex" }}>
                <Tooltip title={t("tooltipTitle.clearFilters")}>
                  <DeleteForeverIcon
                    sx={{
                      color: "#C8017D",
                      paddingRight: "15px",
                      cursor: "pointer",
                    }}
                    onClick={handleClearFilters}
                  />
                </Tooltip>
                <Tooltip title={t("tooltipTitle.search")}>
                  <SearchIcon sx={{ cursor: "pointer" }} onClick={getAllData} />
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </form> */}
      </Box>
      <Box className="carGenericOuterTableBoxTwo boxTableFirst">
        <Box style={containerStyle}>
          <FleetPlanningToolbar
            year={tableHeader}
            subHeader={tableSubHeader}
            yearColor={"#011e41"}
            isExelButton={false}
            isThirdStep={true}
            downloadThirdStepExcel={handleDownloadExcel}
          />
          <Box
            style={gridStyle}
            className="ag-theme-alpine"
            sx={{
              "& .ag-cell": {
                lineHeight: "min(var(--ag-line-height, 40px), 40px)",
              },
            }}
          >
            <AgGridReact
              ref={gridRef}
              domLayout="autoHeight"
              getRowHeight={rowHeight}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowClassRules={rowClassRules}
              rowClass={"backgroundWhite"}
              suppressRowHoverHighlight={true}
              onCellValueChanged={onCellValueChanged}
            />
          </Box>
        </Box>
      </Box>
      {hasScope("SCOPE_UPDATE_CSS_PLANNING") && (
        <Box
          sx={{
            justifyContent: "flex-end",
            display: "flex",
            paddingBottom: "20px",
            paddingRight: "20px",
          }}
        >
          <Mui.Grid container spacing={2} justifyContent="end">
              <Mui.Grid item xs={6} sm={3} lg={1.1}>

              <Mui.Button variant="outlined" onClick={handleResetData} fullWidth startIcon={<Replay />}>
                    <Mui.Typography variant="h6" color={'accent'}>
                    {t("actionButtons.reset")}
                    </Mui.Typography>
                </Mui.Button>

              </Mui.Grid>

              <Mui.Grid item xs={6} sm={3} lg={1.15}>

              <Mui.Button onClick={handleEditData} fullWidth disabled={editedPercentages.length === 0}>
                    <Mui.Typography variant="h6" color={'accent'}>
                    {t("actionButtons.save")}
                    </Mui.Typography>
                </Mui.Button>
              </Mui.Grid>
            </Mui.Grid>

        </Box>
      )}
    </>
  );
};
